import Modal from "@common/controls/Modal/component";
import { useI18n } from "@common/hooks/i18n";
import { EMPTY_ARRAY } from "@common/lib/array";
import { usePromise } from "@common/lib/hooks";
import { i18nFormat } from "@common/lib/i18n";
import { type Affiliate } from "@common/modules/affiliate/types";
import Network from "@common/modules/network/types";
import { getUserDisplayName } from "@common/modules/user/lib";
import * as React from "react";
import { CommissionVolumeContactDrillDownGrid } from "./CommissionVolumeContactDrillDownGrid";
import { CommissionVolumeDrillDownGrid } from "./CommissionVolumeDrillDownGrid";
import Content from "./Content";
import { VolumeDrillDownGrid } from "./VolumeDrillDownGrid";
import { VolumeOrderDrillDownGrid } from "./VolumeOrderDrillDownGrid";

type Props = {
  api: Affiliate.AuditApi;
  className?: string;
  cycle: Affiliate.Cycle;
  isHost?: boolean;
  onClose: () => void;
};
export default (props: Props) => {
  const i18n = useI18n();

  const [drillDown, setDrillDown] = React.useState<"Points" | "Volume" | "CommissionVolume" | null>(null);
  const [orderId, setOrderId] = React.useState<number | null>(null);
  const [userData, setUserData] = React.useState<{ userId: number; userName?: string } | null>(null);

  const [audit] = usePromise<Network.CommissionRules.Api.CommissionAuditData>({
    fn: props.api.commission.getAudit,
    args: [
      {
        genealogyId: props.cycle.genealogyId,
        cycleEndDate: props.cycle.cycleEndDate,
        commissionRuleId: props.cycle.commissionRuleId
      }
    ],
    shouldLoad: true
  });

  const [hierarchyUserVolume] = usePromise<Network.CommissionRules.Api.GenealogyHierarchyUserVolume[]>({
    fn: props.api.commission.getHierarchyUserVolume,
    args: [
      {
        genealogyId: props.cycle.genealogyId,
        cycleEndDate: props.cycle.cycleEndDate,
        commissionRuleId: props.cycle.commissionRuleId
      }
    ],
    shouldLoad: !!audit && drillDown === "Volume"
  });

  const [commissionVolume] = usePromise<Network.CommissionRules.Api.CommissionVolume[]>({
    fn: props.api.commission.getVolume,
    args: [
      {
        genealogyId: props.cycle.genealogyId,
        cycleEndDate: props.cycle.cycleEndDate,
        commissionRuleId: props.cycle.commissionRuleId
      }
    ],
    shouldLoad: !!audit && drillDown === "CommissionVolume"
  });

  const [userVolume] = usePromise<Network.CommissionRules.Api.GenealogyHierarchyUserVolume[]>({
    fn: props.api.commission.getHierarchyVolumeTierPoints,
    args: [
      {
        genealogyId: props.cycle.genealogyId,
        cycleEndDate: props.cycle.cycleEndDate,
        commissionRuleId: props.cycle.commissionRuleId
      }
    ],
    shouldLoad: !!audit && drillDown === "Points"
  });

  const title = React.useMemo(() => {
    if (userData) return userData.userName;
    if (orderId) return i18nFormat(i18n.commissionOrderDrillDownTitleInfo, ["ID", orderId]);

    return audit?.commissionRuleName
      ? `${props.cycle.userFullName}: ${audit?.commissionRuleName}`
      : props.cycle.userFullName;
  }, [i18n, orderId, props.cycle.userFullName, audit?.commissionRuleName, userData]);

  const onBack = React.useCallback(() => {
    if (userData) {
      setUserData(null);
      return;
    }

    if (orderId) {
      setOrderId(null);
      return;
    }

    if (drillDown) setDrillDown(null);
  }, [orderId, drillDown, userData]);

  const onRowClick = React.useCallback((orderNumber: number) => {
    setOrderId(orderNumber);
  }, []);

  const onCommissionRowClick = React.useCallback((userId: number, userName?: string) => {
    setUserData({ userId, userName });
  }, []);

  // TODO: refactor
  return (
    <Modal
      className={props.className}
      hasBackdrop
      isCentered
      isLoading={!audit}
      isVisible
      maxHeight={"80vh"}
      onBack={drillDown ? onBack : undefined}
      onClose={props.onClose}
      width={drillDown ? 800 : 450}
    >
      <Modal.Header title={title} />
      <Modal.Body>
        {audit ? (
          <>
            {!drillDown && (
              <Content
                audit={audit}
                api={props.api}
                cycle={props.cycle}
                isHost={props.isHost}
                onClose={props.onClose}
                onDrillDown={setDrillDown}
              />
            )}

            {drillDown === "Points" && !orderId && (
              <VolumeDrillDownGrid
                data={userVolume ?? EMPTY_ARRAY}
                volumeTypeLabel={audit.volumeTypeLabel}
                onRowClick={(data) => {
                  onRowClick(data.orderNumber);
                }}
              />
            )}

            {drillDown === "Points" && !!orderId && (
              <VolumeOrderDrillDownGrid
                data={userVolume ?? EMPTY_ARRAY}
                orderId={orderId}
                volumeTypeLabel={audit.volumeTypeLabel}
              />
            )}

            {drillDown === "Volume" && !orderId && (
              <VolumeDrillDownGrid
                data={hierarchyUserVolume ?? EMPTY_ARRAY}
                volumeTypeLabel={audit.volumeTypeLabel}
                onRowClick={(data) => {
                  onRowClick(data.orderNumber);
                }}
              />
            )}

            {drillDown === "Volume" && !!orderId && (
              <VolumeOrderDrillDownGrid
                data={hierarchyUserVolume ?? EMPTY_ARRAY}
                orderId={orderId}
                volumeTypeLabel={audit.volumeTypeLabel}
              />
            )}

            {drillDown === "CommissionVolume" && !userData && (
              <CommissionVolumeDrillDownGrid
                data={commissionVolume ?? EMPTY_ARRAY}
                onRowClick={(data) => {
                  onCommissionRowClick(
                    data.userId,
                    getUserDisplayName({ userFirstName: data.userFirstName, userLastName: data.userLastName })
                  );
                }}
              />
            )}

            {drillDown === "CommissionVolume" && !!userData && (
              <CommissionVolumeContactDrillDownGrid data={commissionVolume ?? EMPTY_ARRAY} userId={userData.userId} />
            )}
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};
